import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import {
  Calculate as MathIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowIcon,
  AutoStories as EmptyIcon,
  Refresh as RetryIcon,
  PlayCircleOutline as ContinueIcon,
} from '@mui/icons-material';
import { adminColors } from '../theme/adminTheme';
import { tutorTokens } from '../theme/tutorTheme';
import config, { AGENT_TUTOR_COMING_SOON } from '../config';
import { listAssignedAgents, listSessions } from '../utils/agentTutorStorage';
import { authFetch, handleApiResponse } from '../api/authFetch';
import AgentTutorComingSoon from '../components/agentTutor/AgentTutorComingSoon';

// Only Math is a live subject today (see AgentTutorView), but the mapping
// mirrors the one teachers see in the builder so a new subject just works.
const SUBJECT_ICONS = { math: <MathIcon /> };

const focusRingSx = {
  '&:focus-visible': { outline: `2px solid ${tutorTokens.accent}`, outlineOffset: 2 },
};

// A small, dependency-free relative-time label (no date library is
// installed in this project) — just enough precision for "does this Continue
// chip still deserve my attention," so a session abandoned weeks ago reads
// as stale rather than nagging forever with no context.
function formatRelativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return 'just now';
  if (diffMs < hour) {
    const m = Math.round(diffMs / minute);
    return `${m} min${m === 1 ? '' : 's'} ago`;
  }
  if (diffMs < day) {
    const h = Math.round(diffMs / hour);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  const d = Math.round(diffMs / day);
  if (d < 7) return `${d} day${d === 1 ? '' : 's'} ago`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w} week${w === 1 ? '' : 's'} ago`;
  const mo = Math.round(d / 30);
  return `${mo} month${mo === 1 ? '' : 's'} ago`;
}

// One tutor a student can start working with — a single inviting row (round
// icon, name, an arrow to follow), not the denser status-and-metadata card
// the teacher-facing library uses, since this is the moment a student
// chooses what to spend the next ten minutes on.
const AgentCard = ({ agent, continueSession, onOpen }) => {
  const continueLabel = continueSession?.problem?.display;
  const continueAge = continueSession?.updatedAt ? formatRelativeTime(continueSession.updatedAt) : '';
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };
  const ariaLabel = continueLabel
    ? `Continue ${agent.name} — you were solving ${continueLabel}${continueAge ? `, last worked on ${continueAge}` : ''}`
    : `Start working with ${agent.name}`;
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2.5,
        borderRadius: `${tutorTokens.radiusLg}px`,
        backgroundColor: tutorTokens.surfaceRaised,
        border: `1px solid ${tutorTokens.border}`,
        boxShadow: tutorTokens.shadowCard,
        cursor: 'pointer',
        transition: `border-color ${tutorTokens.durFast}s ease, box-shadow ${tutorTokens.durFast}s ease, transform ${tutorTokens.durFast}s ease`,
        '&:hover': { borderColor: tutorTokens.accent, boxShadow: tutorTokens.shadowHover, transform: 'translateY(-2px)' },
        '&:active': { transform: 'translateY(-1px) scale(0.99)' },
        '@media (prefers-reduced-motion: reduce)': {
          '&:hover': { transform: 'none' },
          '&:active': { transform: 'scale(0.99)' },
        },
        ...focusRingSx,
      }}
    >
      <Box
        sx={{
          width: 46,
          height: 46,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: tutorTokens.accentLight,
          color: tutorTokens.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {SUBJECT_ICONS[agent.subject] || <MathIcon />}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontFamily: tutorTokens.fontUI, ...tutorTokens.type.subtitle, color: tutorTokens.ink }} noWrap>
          {agent.name}
        </Typography>
        <Typography sx={{ ...tutorTokens.type.body, color: tutorTokens.inkSoft }}>
          Mathematics · {agent.grades?.join(', ') || 'K-12'}
        </Typography>
        {continueLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, minWidth: 0 }}>
            <ContinueIcon sx={{ fontSize: 15, color: tutorTokens.accent, flexShrink: 0 }} aria-hidden="true" />
            <Typography
              sx={{ ...tutorTokens.type.label, fontWeight: 700, color: tutorTokens.accent, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              noWrap
            >
              Continue: {continueLabel}
            </Typography>
            {continueAge && (
              <Typography sx={{ ...tutorTokens.type.eyebrow, color: tutorTokens.inkFaint }}>
                · {continueAge}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <ArrowIcon sx={{ color: tutorTokens.accent, flexShrink: 0 }} aria-hidden="true" />
    </Box>
  );
};

// A quiet note, not a CTA slot — there's nothing for a student to click
// here, so this deliberately isn't the accent-circle-plus-heading formula
// the teacher surfaces use. Flat surface, hairline border, icon beside the
// text — same empty-state language as the rest of the product.
const EmptyState = () => (
  <Box
    sx={{
      maxWidth: 460,
      borderRadius: `${tutorTokens.radiusLg}px`,
      backgroundColor: tutorTokens.surfaceRaised,
      border: `1px solid ${tutorTokens.border}`,
      p: { xs: 3, sm: 3.5 },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <EmptyIcon sx={{ fontSize: 22, color: tutorTokens.inkFaint, mt: 0.4, flexShrink: 0 }} aria-hidden="true" />
      <Box>
        <Typography sx={{ fontFamily: tutorTokens.fontUI, ...tutorTokens.type.subtitle, color: tutorTokens.ink, mb: 0.5 }}>
          No tutors assigned yet
        </Typography>
        <Typography sx={{ ...tutorTokens.type.body, color: tutorTokens.inkSoft }}>
          Once a teacher publishes an agent to one of your classes, it'll show up here.
        </Typography>
      </Box>
    </Box>
  </Box>
);

// "Page failed to load" state — same card, icon-circle, and indigo accent
// as StudentTutorSession's and StudentCurriculum's error states, so a
// student moving between pages sees one consistent failure treatment
// instead of three. role="alert" so it's announced the moment it replaces
// the spinner. The body copy is fixed here (not passed in per call site) so
// the one sentence stays identical everywhere it's used, by construction.
const ErrorCard = ({ heading, onRetry }) => (
  <Box
    role="alert"
    sx={{
      maxWidth: 420,
      borderRadius: `${tutorTokens.radiusXl}px`,
      border: `1px solid ${tutorTokens.border}`,
      backgroundColor: tutorTokens.surfaceRaised,
      boxShadow: tutorTokens.shadowCard,
      textAlign: 'center',
      p: { xs: 3, sm: 4 },
      mb: 2.5,
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        mx: 'auto',
        mb: 2,
        borderRadius: '50%',
        backgroundColor: tutorTokens.border,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <RetryIcon sx={{ fontSize: 28, color: tutorTokens.inkSoft }} />
    </Box>
    <Typography sx={{ fontFamily: tutorTokens.fontUI, fontWeight: 600, fontSize: '1.3rem', color: tutorTokens.ink, mb: 1 }}>
      {heading}
    </Typography>
    <Typography sx={{ color: tutorTokens.inkSoft, fontSize: '0.9rem', mb: 3 }}>
      This didn't work. Try again — if it keeps happening, tell your teacher.
    </Typography>
    <Button
      variant="contained"
      startIcon={<RetryIcon />}
      onClick={onRetry}
      sx={{
        minHeight: 44,
        backgroundColor: tutorTokens.accent,
        '&:hover': { backgroundColor: tutorTokens.accentDark },
        '&:active': { transform: 'scale(0.97)' },
      }}
    >
      Try again
    </Button>
  </Box>
);

// Student-facing Agent Tutor library. Per Exhibit A, a student should only
// ever see agents their teacher has assigned to a class they're in — no
// marketplace, no browsing other subjects' agents. /api/agents/assigned
// enforces that server-side by joining the agent's class assignments against
// the caller's own memberships, so the filtering isn't something the client
// could get wrong or bypass.
const StudentTutor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState('');
  const [retryToken, setRetryToken] = useState(0);
  // agentId -> that agent's newest active session, so each card can surface
  // its own "Continue: ..." affordance without a per-card fetch.
  const [activeSessionsByAgent, setActiveSessionsByAgent] = useState({});

  useEffect(() => {
    // Agent Tutor is held behind a "Coming Soon" state for every role — see
    // AGENT_TUTOR_COMING_SOON in config.js. Guard clause, not a removed
    // effect: skip the fetch entirely so no /api/me or /api/agents/assigned
    // request fires while the flag is on. All hooks above still run
    // unconditionally on every render either way.
    if (AGENT_TUTOR_COMING_SOON) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const meRes = await authFetch(`${config.API_BASE_URL}/api/me`);
        const me = await handleApiResponse(meRes, navigate);
        if (me === null || cancelled) return; // handleApiResponse already sent us to /signin on a real auth failure
        setCurrentUser(me);

        try {
          const assigned = await listAssignedAgents();
          if (cancelled) return;
          setAgents(assigned);

          // One call per agent rather than one global call: a global cap
          // (`listSessions(null, N)`, newest-first across every agent) can
          // push an agent's still-active session out of the top N once a
          // student has enough sessions on other agents, silently dropping
          // that agent's "Continue" chip. A student has at most one active
          // session per agent (see get_or_create), so `limit=1` per agent is
          // exact rather than probabilistic. Every call is independently
          // caught so one agent's failure can't blank the rest.
          const sessionLists = await Promise.all(
            assigned.map((a) => listSessions(a.id, 1).catch(() => []))
          );
          if (!cancelled) {
            const byAgent = {};
            assigned.forEach((a, i) => {
              const s = sessionLists[i]?.[0];
              if (s && s.status === 'active') byAgent[a.id] = s;
            });
            setActiveSessionsByAgent(byAgent);
          }
        } catch (e) {
          // A failure to load agents is not a failure to authenticate — show
          // the error rather than bouncing the student to the sign-in page.
          if (!cancelled) setError("We couldn't load your tutors");
        }
      } catch (e) {
        if (cancelled) return;
        // handleApiResponse redirects and returns null on 401 itself - it
        // never throws for that case, so anything caught here (including a
        // non-2xx ApiError) is a real load failure, not an auth problem.
        // Let the student retry instead of bouncing them out as if they'd
        // been signed out.
        setError("We couldn't load this page");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, retryToken]);

  if (AGENT_TUTOR_COMING_SOON) {
    return <AgentTutorComingSoon standalone />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: tutorTokens.surface }}>
        <CircularProgress sx={{ color: tutorTokens.accent }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: tutorTokens.surface }}>
      <Box sx={{ backgroundColor: adminColors.sidebar, color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          role="button"
          tabIndex={0}
          aria-label="Back to chat"
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 } }}
          onClick={() => navigate('/chat')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/chat'); } }}
        >
          <ArrowBackIcon fontSize="small" />
          <Typography sx={{ fontWeight: 800 }}>LearnAndMasterAI</Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          {currentUser?.username?.toUpperCase()} · Student
        </Typography>
      </Box>

      {/* `tutorTokens.layout.maxWidth` — the same content measure every
          other Agent Tutor page now uses (was a one-off 1000 here). */}
      <Box sx={{ maxWidth: tutorTokens.layout.maxWidth, mx: 'auto', p: tutorTokens.layout.gutter }}>
        {/* Explicit fontFamily kept here for parity with
            AgentTutorView/AgentBuilderWizard/MathTutorConfig, which set the
            same tutorTokens.fontUI stack — App.js's global adminTheme now
            also uses Plus Jakarta Sans, so this override is redundant but
            harmless. */}
        <Typography variant="h4" sx={{ fontFamily: tutorTokens.fontUI, fontWeight: 600, color: tutorTokens.ink, mb: 0.5 }}>
          {currentUser?.username ? `Hi ${currentUser.username}, what do you want to work on?` : 'Agent Tutor'}
        </Typography>
        <Typography sx={{ ...tutorTokens.type.bodyLg, color: tutorTokens.inkSoft, mb: 3.5 }}>
          Tutors your teachers built for your classes
        </Typography>

        {error && (
          <ErrorCard heading={error} onRetry={() => setRetryToken((t) => t + 1)} />
        )}

        {!error && agents.length === 0 && <EmptyState />}

        {agents.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
            {agents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                continueSession={activeSessionsByAgent[agent.id]}
                onOpen={() => navigate(`/tutor/${agent.id}`)}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StudentTutor;
